import {
	GQLMerchDecorationTypeType,
	GQLMerchTileFragment,
	GQLOperationalSettingsFragment,
	GQLOrderFragment,
	GQLOrderItemFragment,
	GQLOrderItemPrintDecorationType,
	GQLOrderItemPrintSideType,
	GQLOrderItemTileFragment,
	GQLOrderShippingMethodType,
	GQLOrderStatusType,
	GQLOrderTileFragment,
	GQLProductSideAvailableDecorationType,
	GQLProductSideSideType,
	GQLStoreBaseFragment,
	GQLStoreFragment,
} from '@theprintbar/graphql';
import {
	Breakdown,
	Decoration,
	addDecorationCost,
	addEmbDigitizationCharges,
	addProductDiscount,
	getAdditionalManualPaymentAmount,
	getAdditionalPaymentAmount,
	getAdditionalSystemPaymentAmount,
	getOrderItemQuantity,
	getOrderRefunds,
	getPaidAmount,
	getPaymentAmount,
	getPaymentLineAmount,
	getProductCost,
	getSamedayCost,
	getTotalPaymentAmount,
} from '..';
import { getCurrencyValue, mapEnumValue } from '../../utils';

type OrderPricing = {
	subtotal: number;
	shipping: number;
	additionalCharges: number;
	systemCharges: number;
	manualCharges: number;
	refunds: number;
	refundId?: string;
	discount: number;
	couponCode?: string | null;
	couponDescription?: string | null;
	systemCoupon?: boolean;
	discountPercentage: number;
	total: number;
	paid: number;
	owed: number;
};

export function getOrderPricing(order: GQLOrderTileFragment): OrderPricing {
	return order.store ? getStoreOrderPricing(order) : getWebOrderPricing(order);
}

export function getStoreOrderPricing(order: GQLOrderTileFragment): OrderPricing {
	if (!order.paymentLine) throw new Error('Payment not found');

	const { subtotal, total } = getPaymentLineAmount(order.paymentLine);
	const shipping = getCurrencyValue(order.shippingCharge);
	const discount = getCurrencyValue(subtotal - total);
	const refunds = getOrderRefunds(order);

	return {
		subtotal: Math.max(subtotal - shipping, 0),
		shipping: !total ? 0 : shipping,
		discount,
		discountPercentage: discount ? Math.round((discount / subtotal) * 100) : 0,
		total,
		systemCharges: 0,
		manualCharges: 0,
		paid: order.status === GQLOrderStatusType.PendingPayment ? 0 : total,
		additionalCharges: 0,
		refunds,
		owed: order.status === GQLOrderStatusType.PendingPayment ? total : 0,
	};
}

export function getWebOrderPricing(order: GQLOrderTileFragment): OrderPricing {
	if (!order.payment) throw new Error('Payment not found');
	const { subtotal } = getPaymentAmount(order.payment);

	const shipping = getCurrencyValue(order.shippingCharge);
	const additionalCharges = getAdditionalPaymentAmount(order.payment);
	const systemCharges = getAdditionalSystemPaymentAmount(order.payment);
	const manualCharges = getAdditionalManualPaymentAmount(order.payment);

	const refunds = getOrderRefunds(order);
	const discount = getCurrencyValue(order.payment.coupon?.value || 0);
	const total = getTotalPaymentAmount(order.payment);
	const paid = getPaidAmount(order, order.payment);

	return {
		subtotal: subtotal - shipping,
		shipping,
		additionalCharges,
		systemCharges,
		manualCharges,
		discount,
		discountPercentage: discount ? Math.round((discount / subtotal) * 100) : 0,
		couponCode: order.payment.coupon?.coupon.code,
		couponDescription: order.payment.coupon?.coupon.shortDescription,
		systemCoupon: !!order.payment.coupon?.coupon.systemCoupon,
		refunds,
		total,
		paid,
		owed: getCurrencyValue(Math.max(0, total - paid)),
	};
}

interface GetOrderItemPricingProps {
	order: GQLOrderFragment;
	orderItem: GQLOrderItemTileFragment;
	settings: GQLOperationalSettingsFragment;
	store?: GQLStoreBaseFragment | null;
}

export function getOrderItemPricing(props: GetOrderItemPricingProps) {
	const { order, orderItem, settings, store } = props;
	const quantity = getOrderItemQuantity(orderItem);

	const itemBreakdown = orderItem.breakdown?.structure?.filter(Boolean) || [];

	if (!quantity || !orderItem.product) {
		return { breakdown: [], quantity, subtotal: 0, total: 0, singlePrice: 0 };
	} else if (itemBreakdown.length) {
		const subtotal = getCurrencyValue(itemBreakdown.reduce((acc, b) => acc + getCurrencyValue(b?.amount), 0));
		if (subtotal > 0) {
			return {
				breakdown: itemBreakdown,
				quantity,
				subtotal,
				singlePrice: getCurrencyValue(subtotal / quantity),
				total: subtotal,
			};
		}
	}

	let breakdown: Breakdown[] = [getProductCost({ product: orderItem.product, quantity, store })];

	if (order.sameday && !store) {
		breakdown.push(getSamedayCost({ quantity, settings }));
	}

	orderItem.prints?.forEach(print => {
		breakdown = addDecorationCost({
			breakdown,
			side: print.side,
			colourCount: print.requestedColours || 0,
			decoration: print,
			quantity,
			settings,
			store,
			product: orderItem.product!,
			dontCheckArtwork: true,
		});
	});

	const embDecorations =
		orderItem.prints?.filter(p => p.decoration === GQLOrderItemPrintDecorationType.Emb && !p.useSuppliedEmbFile)?.length || 0;

	if (embDecorations) {
		breakdown = addEmbDigitizationCharges({ breakdown, embDecorations, product: orderItem.product!, settings, store, quantity });
	}

	let subtotal = getCurrencyValue(breakdown.reduce((acc, { amount }) => acc + amount, 0));
	//THIS MUST COME LAST
	breakdown = addProductDiscount({ breakdown, product: orderItem.product, quantity, ...props });
	let total = getCurrencyValue(breakdown.reduce((acc, { amount }) => acc + amount, 0));
	return {
		breakdown,
		quantity,
		subtotal,
		singlePrice: getCurrencyValue(subtotal / quantity),
		total,
	};
}
